import { useEffect, useRef, useState, type PointerEvent } from 'react';
import { formatDuration } from '../lib/format';

const fmt = (ms: number) => formatDuration(Math.max(0, Math.round(ms / 1000)));

/** Voice message: play/pause, seekable progress bar, elapsed / total time. */
export function VoicePlayer({ src, durationMs, mine }: { src: string; durationMs?: number | null; mine: boolean }) {
  const audio = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [pos, setPos] = useState(0); // ms
  const [total, setTotal] = useState(durationMs ?? 0);

  useEffect(() => {
    const a = audio.current;
    if (!a) return;
    const onTime = () => setPos(a.currentTime * 1000);
    const onMeta = () => Number.isFinite(a.duration) && a.duration > 0 && setTotal(a.duration * 1000);
    const onEnd = () => {
      setPlaying(false);
      setPos(0);
    };
    const onPause = () => setPlaying(false);
    const onPlay = () => {
      setPlaying(true);
      // Only one voice message plays at a time.
      document.querySelectorAll<HTMLAudioElement>('audio[data-voice]').forEach((o) => o !== a && o.pause());
    };
    a.addEventListener('timeupdate', onTime);
    a.addEventListener('loadedmetadata', onMeta);
    a.addEventListener('ended', onEnd);
    a.addEventListener('pause', onPause);
    a.addEventListener('play', onPlay);
    return () => {
      a.removeEventListener('timeupdate', onTime);
      a.removeEventListener('loadedmetadata', onMeta);
      a.removeEventListener('ended', onEnd);
      a.removeEventListener('pause', onPause);
      a.removeEventListener('play', onPlay);
    };
  }, []);

  const toggle = () => {
    const a = audio.current;
    if (!a) return;
    if (a.paused) void a.play().catch(() => setPlaying(false));
    else a.pause();
  };

  const seek = (e: PointerEvent<HTMLDivElement>) => {
    const a = audio.current;
    if (!a || !total) return;
    const r = e.currentTarget.getBoundingClientRect();
    const t = (Math.min(Math.max(e.clientX - r.left, 0), r.width) / r.width) * (total / 1000);
    a.currentTime = t;
    setPos(t * 1000);
  };

  const progress = total ? Math.min(1, pos / total) : 0;
  return (
    <div className={`voice${mine ? ' mine' : ''}`}>
      <audio ref={audio} src={src} preload="metadata" data-voice />
      <button className="voice-btn" onClick={toggle} aria-label={playing ? 'Пауза' : 'Воспроизвести'}>
        {playing ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <rect x="6" y="5" width="4" height="14" rx="1" />
            <rect x="14" y="5" width="4" height="14" rx="1" />
          </svg>
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M8 5.5v13a1 1 0 0 0 1.5.86l10.5-6.5a1 1 0 0 0 0-1.72L9.5 4.64A1 1 0 0 0 8 5.5z" />
          </svg>
        )}
      </button>
      <div className="voice-body">
        <div className="voice-track" onPointerDown={seek} role="slider" aria-label="Позиция" aria-valuenow={Math.round(progress * 100)}>
          <div className="voice-fill" style={{ width: `${progress * 100}%` }} />
        </div>
        <span className="voice-time">{playing || pos > 0 ? fmt(pos) : fmt(total)}</span>
      </div>
    </div>
  );
}
